import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { FuseNavigationService } from "@fuse/components/navigation/navigation.service";
import { CredentialsTO } from "app/data/schema/Identity/CredentialsTO";
import { EnumGrantType } from "app/data/schema/Identity/EnumGrantType";
import { LoginResultTO } from "app/data/schema/Identity/LoginResultTO";
import { PatchChangePasswordTO } from "app/data/schema/Identity/PatchChangePasswordTO";
import { RecuperarSenhaTO } from "app/data/schema/Identity/RecuperarSenhaTO";
import { SessionDTO } from "app/data/schema/Identity/SessionDTO";
import { TokenPayload } from "app/data/schema/Identity/TokenPayload";
import { TrocarSenhaDTO } from "app/data/schema/Identity/TrocarSenhaDTO";
import { UsuarioDTO } from "app/data/schema/User/UsuarioDTO";
import { ApiUsersService } from "app/data/service/api-users.service";
import { NavigationService } from "app/navigation/navigation.service";
import { environment } from "environments/environment";
import decode from "jwt-decode";
import { CookieService } from "ngx-cookie-service";
import { BehaviorSubject, Observable, of, throwError } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { SubscriptionService } from "./subscription.service";

@Injectable({
    providedIn: "root"
})
export class AuthService {
    private session$: BehaviorSubject<SessionDTO>;
    public sessionAsync: Observable<SessionDTO>;

    private loggedUser$: BehaviorSubject<UsuarioDTO>;
    public loggedUserAsync: Observable<UsuarioDTO>;

    constructor(
        private _subscriptionService: SubscriptionService,
        private _httpClient: HttpClient,
        private _fuseNavigationService: FuseNavigationService,
        private _cookieService: CookieService,
        private _navigationService: NavigationService,
        private userService: ApiUsersService
    ) {
        const sessionCookie = this._cookieService.get("session");
        if (sessionCookie) {
            localStorage.setItem("session", atob(decodeURIComponent(sessionCookie)));
            this._cookieService.delete("session");
        }

        let sessionCallback = this._cookieService.get("sessionCallback");
        if (sessionCallback) {
            sessionCallback = atob(decodeURIComponent(sessionCallback));
            console.log(`sessionCallback=${sessionCallback}`);
            this._cookieService.delete("sessionCallback");
            window.location.href = sessionCallback;
        }

        this.session$ = new BehaviorSubject<SessionDTO>(
            (JSON.parse(localStorage.getItem("session")) as SessionDTO) || (null as SessionDTO)
        );

        this.loggedUser$ = new BehaviorSubject<UsuarioDTO>(null);

        if (!this.session$.getValue()?.access_token) this.session$.next(null);

        this.sessionAsync = this.session$.asObservable();
        this.loggedUserAsync = this.loggedUser$.asObservable();
    }

    public getLoggedUser(): void {
        if (!this.getClaimsUserId()) return;
        this.userService.getById(this.getClaimsUserId()).subscribe((user) => this.loggedUser$.next(user));
    }

    public isAuthenticated = (): boolean => {
        const userId: string = this.getClaimsUserId();
        return userId !== undefined && userId !== null;
    };

    private login(credentialsTO: CredentialsTO): Observable<SessionDTO> {
        return this._httpClient.post<SessionDTO>(
            `${environment.identity.url}/api/v1/Auth/login`,
            JSON.stringify(credentialsTO),
            {
                headers: {
                    "Content-Type": "application/json; charset=utf-8"
                }
            }
        );
    }

    loginPassword(username: string, password: string): Observable<LoginResultTO> {
        return new Observable<LoginResultTO>((observable) =>
            this.login({
                grant_type: EnumGrantType.password,
                username: username.toLowerCase(),
                password,
                client_id: environment.identity.client_id,
                subscriptionId: this._subscriptionService.currentSubscription?.id || environment.default_subscription_id
            } as CredentialsTO)
                .pipe(
                    tap((session) => {
                        this.setSession(session);

                        observable.next({
                            sucess: true
                        } as LoginResultTO);

                        //reload new Menu Items
                        this._fuseNavigationService.unregister("default");
                        this._navigationService.getNavigationByLoggedUser(this.getClaimsRoles()).subscribe((nav) => {
                            this._fuseNavigationService.register("default", nav);
                            this._fuseNavigationService.setCurrentNavigation("default");
                        });
                    })
                )
                .subscribe(
                    () => observable.complete(),
                    (err) => observable.error(err)
                )
        );
    }

    recuperarSenha(email: string): Observable<SessionDTO> {
        const recuperarSenhaTO: RecuperarSenhaTO = {
            subscriptionId: this._subscriptionService.currentSubscription?.id || environment.default_subscription_id,
            username: email
        };

        return this._httpClient.post<SessionDTO>(
            `${environment.identity.url}/api/v1/auth/password/recover`,
            JSON.stringify(recuperarSenhaTO),
            {
                headers: {
                    "Content-Type": "application/json; charset=utf-8"
                }
            }
        );
    }

    trocarSenha(token: string, novaSenha: string): Observable<any> {
        const trocarSenhaTO: TrocarSenhaDTO = {
            subscriptionId: this._subscriptionService.currentSubscription?.id || environment.default_subscription_id,
            token: token,
            novaSenha: novaSenha
        };
        return this._httpClient.post<SessionDTO>(
            `${environment.identity.url}/api/v1/auth/password`,
            JSON.stringify(trocarSenhaTO),
            {
                headers: {
                    "Content-Type": "application/json; charset=utf-8"
                }
            }
        );
    }

    logout(): Observable<boolean> {
        return new Observable<boolean>((observable) => {
            this.session$.next(null);
            localStorage.clear();

            observable.next(true);
            observable.complete();
        });
    }

    getDateEx(): Date {
        return new Date(
            new Date(this.session$.getValue().createDate).getTime() + this.session$.getValue().expires_in * 1000
        );
    }

    getAccessToken(): string {
        if (!this.session$.getValue()?.access_token) {
            return null;
        }

        return this.session$.getValue()?.access_token;
    }

    getDecodedToken(): TokenPayload | null {
        const token = this.getAccessToken();

        if (!token) return null;

        const tokenPayload: TokenPayload = decode(token) as TokenPayload;
        return tokenPayload;
    }

    getClaimsUserId(): string {
        const decodedClaims = this.getDecodedClaimsFromSession();

        if (decodedClaims) return decodedClaims.userId;

        return null;
    }

    getClaimsName(): string {
        const decodedClaims = this.getDecodedClaimsFromSession();

        if (decodedClaims) return decodedClaims.sub;

        return null;
    }

    getClaimsRoles(): string[] {
        const token = this.getDecodedToken();

        if (!token) return [];

        if (typeof token.roles === "string") {
            return [token.roles];
        }

        return token.roles as string[];
    }

    public setLastLogin(): void {
        localStorage.setItem(`lastaccess_${this.getClaimsUserId()}`, Date.now().toString());
    }

    public triggerUserMatch(): boolean {
        const lastAccess = new Date(localStorage.getItem(`lastaccess_${this.getClaimsUserId()}`));
        lastAccess.setDate(lastAccess.getDate() + 1);

        return lastAccess.getDate() < Date.now();
    }

    private getDecodedClaimsFromSession = (): any => {
        // this.session$.next(JSON.parse(localStorage.getItem("session")) as SessionDTO);

        if (this.session$.getValue()?.access_token == null) {
            return null;
        }

        const base64Url = this.session$.getValue().access_token.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split("")
                .map(function (c) {
                    return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
                })
                .join("")
        );

        return JSON.parse(jsonPayload);
    };

    getClaimsSub(): string {
        const decodedClaims = this.getDecodedClaimsFromSession();

        if (decodedClaims) return decodedClaims.sub;

        return null;
    }

    getClaimsUserName(): string {
        const decodedClaims = this.getDecodedClaimsFromSession();

        if (decodedClaims) return decodedClaims.username;

        return null;
    }

    public getCurrentSubscriptionId = (): string => {
        let currentSubscriptionId = localStorage.getItem(`currentSubscriptionId_${location.hostname}`);

        if (!currentSubscriptionId)
            this._subscriptionService.currentSubscription$.subscribe(
                (subscription) => (currentSubscriptionId = subscription.id)
            );

        return currentSubscriptionId;
    };

    private getClaimsSubscriptionId = (): string => {
        const decodedClaims = this.getDecodedClaimsFromSession();

        if (decodedClaims) return decodedClaims.subscriptionId;

        return null;
    };

    private refreshToken(): Observable<SessionDTO> {
        if (!this.session$.getValue()) return throwError(new Error("Session invalid"));

        const credentialsTO = {
            grant_type: EnumGrantType.refresh_token,
            refresh_token: this.session$.getValue().refresh_token,
            client_id: environment.identity.client_id,
            subscriptionId: this._subscriptionService.currentSubscription?.id || environment.default_subscription_id
        };

        return this._httpClient.post<SessionDTO>(
            `${environment.identity.url}/api/v1/Auth/token`,
            JSON.stringify(credentialsTO),
            {
                headers: {
                    "Content-Type": "application/json; charset=utf-8"
                }
            }
        );
    }

    public applyRefreshToken(): Observable<LoginResultTO> {
        return new Observable<LoginResultTO>((observable) =>
            this.refreshToken()
                .pipe(
                    tap((session) => {
                        if (session) {
                            session.createDate = new Date();
                            this.session$.next(session);

                            localStorage.setItem("session", JSON.stringify(this.session$));

                            observable.next({
                                sucess: true
                            } as LoginResultTO);
                        } else {
                            this.session$.next(null);
                            localStorage.clear();

                            observable.next({
                                sucess: false,
                                message: "Token expired :("
                            } as LoginResultTO);
                        }
                    }),
                    catchError((error) =>
                        of(
                            observable.next({
                                sucess: false,
                                message: error
                            } as LoginResultTO)
                        )
                    )
                )
                .subscribe()
        );
    }

    public updateUserPassword(data: PatchChangePasswordTO): Observable<any> {
        return this._httpClient.patch<any>(
            `${environment.identity.url}/api/v1/users/${this.getClaimsUserId()}/password`,
            data
        );
    }

    public requestUserEmailUpdate(newEmail: string): Observable<any> {
        const data = {
            novoEmail: newEmail
        };

        return this._httpClient.patch<any>(
            `${environment.identity.url}/api/v1/users/${this.getClaimsUserId()}/email`,
            data
        );
    }

    public redefinirEmail(token: string): Observable<any> {
        const data = {
            token: token
        };

        return this._httpClient.post<any>(
            `${environment.identity.url}/api/v1/users/${this.getClaimsUserId()}/emailChecked`,
            data
        );
    }

    public setSession(session: SessionDTO): void {
        session.createDate = new Date();
        this.session$.next(session);
        localStorage.setItem("session", JSON.stringify(session));
    }
}
